import { IkeToken, rotationForDefaultAssetType } from '@ikegps/core-api';
import type { CollectionModelFieldAssetType } from '@ikegps/core-shared';
import { Services } from '../../../services';
import { AssetTypeSwatch } from '../../../components/asset.type.swatch';
import { Icon } from '../../../components/icon';
import { MaterialIcon } from '../../../components/icon/icons';
import { InputState } from '../../../components/input/input.state';
import {
	CSS as MultiListInputCSS,
	Style as MultiListInputStyle,
} from '../../../components/input/nested.list.input.style';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import { openAssetTypeModal } from '../../asset.type.modal/asset.type.modal';
import type { CollectionEditCtrl } from '../collection.view.ctrl';
import type { CollectionFieldView } from './field.view';

// The edit-mode row reuses select/nested-list's own classes+styles (imported above) so it looks and
// behaves like those fields' chooser button, rather than a bespoke line-tool-style action button.
const CSS = {
	container: 'c-AssetTypeField',
	typeRow: 'c-AssetTypeField__typeRow',
	typeLabel: 'c-AssetTypeField__typeLabel',
	icon: 'c-AssetTypeField__icon',
	error: 'c-AssetTypeField__error',
};

const ELLIPSIS = { overflow: 'hidden', 'white-space': 'nowrap', 'text-overflow': 'ellipsis' } as const;

addStyles(MultiListInputStyle);
addStyles({
	[CSS.container]: { display: 'block', width: '100%' },
	// Read-only inline row (view mode) — the edit-mode row below reuses MultiListInputCSS instead.
	[CSS.typeRow]: { display: 'flex', 'align-items': 'center', gap: DefaultTheme.space(2), 'min-width': 0 },
	[CSS.typeLabel]: { 'min-width': 0, ...ELLIPSIS },
	[CSS.icon]: { display: 'flex', 'align-items': 'center', 'flex-shrink': 0, 'margin-right': DefaultTheme.space(2) },
	[CSS.error]: { color: DefaultTheme.font.color.error, 'font-size': '12px', 'margin-top': DefaultTheme.space(1) },
});

/**
 * Renders one asset type field. Holds the field so the row/pick helpers read it off `this` instead of
 * threading it through every call — a fresh instance is created per interface call, mirroring LineFieldRenderer.
 */
class AssetTypeFieldRenderer {
	constructor(
		private readonly field: CollectionModelFieldAssetType,
		private readonly requiredState?: InputState<string>,
		// Non-Pro departments (no FEATURE_ASSET_TYPE) may only pick the default "Collection" type — UFO-1733.
		private readonly onlyCollection: boolean = false
	) {}

	viewInline(): MithrilVNode {
		const title = this.field.value;
		if (title == null) {
			return null;
		}
		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.typeRow }, [
				AssetTypeSwatch.view({
					icon: this.field.icon,
					color: this.field.color,
					rotate: rotationForDefaultAssetType(title),
				}),
				m('span', { className: CSS.typeLabel }, title),
			]),
		]);
	}

	viewEdit(): MithrilVNode {
		// In a Pro department the picker offers every visible type; in a non-Pro one it offers only the
		// default "Collection" type (via `onlyCollection`), so the field stays editable and re-selectable
		// after clearing rather than locked (UFO-1733).
		return m('div', { className: CSS.container }, [
			this.viewTypeSelect(),
			// An asset type is structurally always-required (every consumer — map markers, the legend, KML,
			// shapefile — assumes a resolvable value), so this isn't gated by the field schema's optional
			// `required` toggle. Shown whenever cleared, so the disabled Save button (see `requiredState`'s
			// registration in `AssetTypeFieldView.viewEdit`) isn't a mystery.
			this.field.hasValue() ? null : m('div', { className: CSS.error }, Translate(T.INPUT_REQUIRED)),
		]);
	}

	/** Same chooser-button look as select/nested list (MultiListInputCSS) — a plain row with a trailing chevron. */
	private viewTypeSelect(): MithrilVNode {
		const title = this.field.value;
		const hasType = title != null;
		const label = hasType ? title : null;
		const rotate = title != null ? rotationForDefaultAssetType(title) : undefined;
		return m('div', { className: MultiListInputCSS.container }, [
			m(
				'button',
				{
					type: 'button',
					className: `${MultiListInputCSS.button} mdl-button`,
					onclick: () => void this.pickType(),
				},
				[
					hasType
						? m(
								'span',
								{ className: CSS.icon },
								AssetTypeSwatch.view({ icon: this.field.icon, color: this.field.color, size: 20, rotate })
							)
						: null,
					m('span', { className: MultiListInputCSS.label }, label),
					Icon.view(MaterialIcon.FILTER_NONE, { className: MultiListInputCSS.edit }),
				]
			),
		]);
	}

	private async pickType() {
		const chosen = await openAssetTypeModal(this.field.schema, this.field.value ?? '', this.onlyCollection);
		if (chosen != null) {
			this.field.Mutator.manual(chosen);
			this.requiredState?.setValue(chosen);
		}
	}
}

export const AssetTypeFieldView: CollectionFieldView<CollectionModelFieldAssetType> = {
	viewInline(field: CollectionModelFieldAssetType): MithrilVNode {
		return new AssetTypeFieldRenderer(field).viewInline();
	},

	viewDetail(): MithrilVNode {
		return null;
	},

	hasClear(field: CollectionModelFieldAssetType): boolean {
		return field.hasValue();
	},

	viewEdit(field: CollectionModelFieldAssetType, ctrl: CollectionEditCtrl): MithrilVNode {
		// Asset type has no schema-configurable "required" toggle (it's an intrinsic invariant of the field
		// type, not an admin choice) — this registers a hardcoded-required InputState the same way every
		// other editable field does, so an emptied asset type disables Save through the existing
		// `ctrl.changeState.isValid` pipeline instead of needing a bespoke save-gate.
		let requiredState = ctrl.changeState?.getState<string>(field.id);
		if (requiredState == null) {
			requiredState = new InputState<string>(field.schema.displayName, field.id);
			requiredState.isRequired = true;
			requiredState.setValue(field.value);
			ctrl.changeState?.addState(field.id, requiredState);
		}

		// Standard (non-Pro) departments lack FEATURE_ASSET_TYPE: keep the field editable but restrict the
		// picker to the default "Collection" type so a required asset type can be re-set after clearing (UFO-1733).
		const onlyCollection = !Services.Token.has(IkeToken.FEATURE_ASSET_TYPE);
		return new AssetTypeFieldRenderer(field, requiredState, onlyCollection).viewEdit();
	},
};
